import { SOLANA_CHAIN_ID, tryAssetIdLikeToLegacyAssetId } from "@repo/caip";
import {
  NATIVE_TOKEN_INTERNAL_PLACEHOLDER_ADDRESS,
  WSOL_SOLANA_ADDRESS,
} from "@repo/constants";
import { type PlatformApiTrpcClient } from "~/lib/trpc/platform-api-adapter";
import {
  coerceBoolean,
  coerceFiniteInteger,
  coerceFiniteNumber,
  type PortfolioAllocationHistoryCheckpointModel,
  type PortfolioAssetModel,
  type PortfolioAssetPriceModel,
  type PortfolioControlPlaneSummaryModel,
  type PortfolioControlPlaneTargetType,
  type PortfolioConvergenceSummaryModel,
  type PortfolioInstanceModel,
  type PortfolioJsonValue,
  type PortfolioPerformancePointModel,
  type PortfolioPerformanceSeriesPointModel,
  type PortfolioPositionModel,
  type PortfolioReviewQueueItemModel,
  type PortfolioReviewQueueSummaryModel,
  normalizePortfolioAddress,
  parsePortfolioAssetId,
} from "./models";

const EVM_NATIVE_SLIP44_REFERENCE = "60";

const SOLANA_NATIVE_SLIP44_REFERENCE = "501";

export const PERFORMANCE_LIVE_POSITION_SOURCE = "performance-live";

const STRICT_DECIMAL_PATTERN = /^(?:0|[1-9]\d*)(?:\.\d+)?$/;

export type DashboardNormalizedResponse = Awaited<
  ReturnType<
    PlatformApiTrpcClient["strategyInstances"]["getDashboardNormalizedData"]["query"]
  >
>;

export type PortfolioDetailNormalizedResponse = Awaited<
  ReturnType<
    PlatformApiTrpcClient["strategyInstances"]["getPortfolioDetailNormalizedData"]["query"]
  >
>;

export type PortfolioPerformanceSeriesResponse = Awaited<
  ReturnType<
    PlatformApiTrpcClient["v2"]["portfolio"]["performance"]["series"]["query"]
  >
>;

type DashboardPerformanceSeriesResponse =
  DashboardNormalizedResponse["performanceSeries"][number];

export type PortfolioPerformanceSeriesPayload =
  | DashboardPerformanceSeriesResponse
  | PortfolioPerformanceSeriesResponse;

export type PortfolioAllocationHistoryChartResponse = Awaited<
  ReturnType<
    PlatformApiTrpcClient["v2"]["public"]["portfolio"]["activity"]["allocationHistoryChart"]["query"]
  >
>;

export function applyOwnershipFallback(
  portfolio: PortfolioInstanceModel,
  principalOwnerAddressSet: ReadonlySet<string>,
): PortfolioInstanceModel {
  const isPrincipalOwner = principalOwnerAddressSet.has(
    normalizePortfolioAddress(portfolio.ownerAddress),
  );
  const viewerIsOwner = portfolio.viewerIsOwner ?? isPrincipalOwner;
  const viewerCanManage = portfolio.viewerCanManage ?? viewerIsOwner;

  return {
    ...portfolio,
    viewerCanManage,
    viewerIsOwner,
  };
}

export function normalizePortfolioInstanceModel(
  portfolio: DashboardNormalizedResponse["portfolioInstances"][number],
): PortfolioInstanceModel {
  const portfolioRecord = asRecord(portfolio);
  const strategyData = readJsonValue(portfolioRecord?.strategyData);
  const ownerAccountIndex = readBigIntString(
    portfolioRecord?.ownerAccountIndex ?? portfolioRecord?.owner_account_index,
  );

  return {
    ...portfolio,
    ...(strategyData !== undefined ? { strategyData } : {}),
    controlPlane: mapControlPlaneSummaryModel(portfolio.controlPlane),
    ownerAccountIndex: ownerAccountIndex ?? null,
  };
}

export function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  return value as Record<string, unknown>;
}

export function readString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

export function readNonEmptyString(value: unknown): string | undefined {
  const stringValue = readString(value)?.trim();
  return stringValue && stringValue.length > 0 ? stringValue : undefined;
}

function readBigIntString(value: unknown): string | undefined {
  if (typeof value === "bigint") {
    return value >= 0n ? value.toString() : undefined;
  }

  if (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 0
  ) {
    return String(value);
  }

  const stringValue = readNonEmptyString(value);
  return stringValue && /^\d+$/.test(stringValue) ? stringValue : undefined;
}

function isJsonValue(value: unknown): value is PortfolioJsonValue {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return true;
  }

  if (Array.isArray(value)) {
    return value.every((item) => isJsonValue(item));
  }

  if (typeof value === "object") {
    return Object.values(value).every((item) => isJsonValue(item));
  }

  return false;
}

function readJsonValue(value: unknown): PortfolioJsonValue | null | undefined {
  if (value === undefined) {
    return undefined;
  }

  return isJsonValue(value) ? value : null;
}

export function readTimestampMs(value: unknown): number | undefined {
  if (value instanceof Date) {
    return value.getTime();
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (trimmedValue.length === 0) {
      return undefined;
    }

    // Parse ISO-like values as dates before numeric coercion.
    if (!/^-?\d+(\.\d+)?$/.test(trimmedValue)) {
      const parsedDate = Date.parse(trimmedValue);
      if (Number.isFinite(parsedDate)) {
        return parsedDate;
      }
    }
  }

  const asNumber = coerceFiniteNumber(
    typeof value === "number" || typeof value === "string" ? value : undefined,
  );
  if (asNumber !== undefined) {
    return asNumber < 10_000_000_000 ? asNumber * 1_000 : asNumber;
  }

  const asString = readString(value);
  if (!asString) {
    return undefined;
  }

  const parsed = Date.parse(asString);
  if (!Number.isFinite(parsed)) {
    return undefined;
  }

  return parsed;
}

function readPortfolioRunStatus(
  value: unknown,
): PortfolioConvergenceSummaryModel["executionStatus"] {
  const normalizedValue = readString(value);

  switch (normalizedValue) {
    case "accepted":
    case "queued":
    case "running":
    case "awaiting_user":
    case "completed":
    case "failed":
    case "cancelled":
      return normalizedValue;
    default:
      return undefined;
  }
}

function readApprovalStatus(
  value: unknown,
): PortfolioReviewQueueItemModel["status"] | undefined {
  const normalizedValue = readString(value);

  switch (normalizedValue) {
    case "pending":
    case "approved":
    case "rejected":
    case "dismissed":
    case "blocked":
    case "failed":
    case "superseded":
      return normalizedValue;
    default:
      return undefined;
  }
}

function readControlPlaneTargetType(
  value: unknown,
): PortfolioControlPlaneTargetType | undefined {
  const normalizedValue = readString(value);

  if (normalizedValue === "portfolio_update") {
    return normalizedValue;
  }

  return undefined;
}

function readConvergenceState(
  value: unknown,
): PortfolioConvergenceSummaryModel["state"] | undefined {
  const normalizedValue = readString(value);

  switch (normalizedValue) {
    case "awaiting_review":
    case "queued":
    case "moving":
    case "blocked":
    case "aligned":
    case "failed":
      return normalizedValue;
    default:
      return undefined;
  }
}

export function mapReviewQueueItemModel(
  rawItem: unknown,
): PortfolioReviewQueueItemModel | undefined {
  const item = asRecord(rawItem);
  const approvalId = readString(item?.approvalId);
  const status = readApprovalStatus(item?.status);
  const summary = readString(item?.summary);
  const targetId = readString(item?.targetId);
  const targetType = readControlPlaneTargetType(item?.targetType);
  const title = readString(item?.title);
  const updatedAt = readTimestampMs(item?.updatedAt);

  if (
    !approvalId ||
    !status ||
    !summary ||
    !targetId ||
    !targetType ||
    !title ||
    updatedAt === undefined
  ) {
    return undefined;
  }

  return {
    approvalId,
    status,
    summary,
    targetId,
    targetType,
    title,
    updatedAt,
  };
}

export function mapConvergenceSummaryModel(
  rawConvergence: unknown,
): PortfolioConvergenceSummaryModel | undefined {
  const convergence = asRecord(rawConvergence);
  const state = readConvergenceState(convergence?.state);
  const summary = readString(convergence?.summary);
  const updatedAt = readTimestampMs(convergence?.updatedAt);

  if (!state || !summary || updatedAt === undefined) {
    return undefined;
  }

  return {
    approvalId: readString(convergence?.approvalId),
    executionStatus: readPortfolioRunStatus(convergence?.executionStatus),
    proposalId: readString(convergence?.proposalId),
    runId: readString(convergence?.runId),
    state,
    summary,
    targetId: readString(convergence?.targetId),
    targetType: readControlPlaneTargetType(convergence?.targetType) ?? null,
    updatedAt,
  };
}

export function mapReviewQueueSummaryModel(
  rawReviewQueue: unknown,
): PortfolioReviewQueueSummaryModel | undefined {
  const reviewQueue = asRecord(rawReviewQueue);
  if (!reviewQueue) {
    return undefined;
  }

  const latestItem =
    mapReviewQueueItemModel(reviewQueue.latestItem) ??
    mapReviewQueueItemModel(reviewQueue.latestProposal);

  return {
    activeApprovalCount: coerceFiniteInteger(
      reviewQueue.activeApprovalCount ?? reviewQueue.activeProposalCount,
      0,
    ),
    awaitingReviewCount: coerceFiniteInteger(
      reviewQueue.awaitingReviewCount,
      0,
    ),
    blockedCount: coerceFiniteInteger(reviewQueue.blockedCount, 0),
    failedCount: coerceFiniteInteger(reviewQueue.failedCount, 0),
    ...(latestItem ? { latestItem } : {}),
    movingCount: coerceFiniteInteger(reviewQueue.movingCount, 0),
    queuedCount: coerceFiniteInteger(reviewQueue.queuedCount, 0),
  };
}

export function mapControlPlaneSummaryModel(
  rawControlPlane: unknown,
): PortfolioControlPlaneSummaryModel | undefined {
  const controlPlane = asRecord(rawControlPlane);
  if (!controlPlane) {
    return undefined;
  }

  const convergence = mapConvergenceSummaryModel(controlPlane.convergence);
  const reviewQueue = mapReviewQueueSummaryModel(
    controlPlane.reviewQueue ?? controlPlane.inbox,
  );

  if (!convergence || !reviewQueue) {
    return undefined;
  }

  return {
    convergence,
    reviewQueue,
  };
}

export function mapPerformancePointModels(
  portfolioId: string,
  rawPoints: PortfolioDetailNormalizedResponse["performancePoints"],
): PortfolioPerformancePointModel[] {
  if (!Array.isArray(rawPoints)) {
    return [];
  }

  const mappedPoints: PortfolioPerformancePointModel[] = [];
  for (const [index, rawPoint] of rawPoints.entries()) {
    const point = asRecord(rawPoint);
    if (!point) {
      continue;
    }

    const timestamp = readTimestampMs(point.timestamp);
    const valueUsd = coerceFiniteNumber(
      typeof point.valueUsd === "number" || typeof point.valueUsd === "string"
        ? point.valueUsd
        : undefined,
    );

    if (timestamp === undefined || valueUsd === undefined) {
      continue;
    }

    const fallbackId = `${portfolioId}:${timestamp}:${index}`;
    const id = readString(point.id) ?? fallbackId;
    const flowsCountFromArray = Array.isArray(point.flows)
      ? point.flows.length
      : 0;

    mappedPoints.push({
      flowsCount: coerceFiniteInteger(point.flowsCount, flowsCountFromArray),
      id,
      isAggregate: coerceBoolean(point.isAggregate, false),
      portfolioId,
      timestamp,
      valueUsd,
    });
  }

  return mappedPoints;
}

export function mapPerformanceSeriesModels(
  portfolioId: string,
  payload: PortfolioPerformanceSeriesPayload | null,
): PortfolioPerformanceSeriesPointModel[] {
  const rawSeries = payload?.series;
  if (!Array.isArray(rawSeries)) {
    return [];
  }

  const mappedSeries: PortfolioPerformanceSeriesPointModel[] = [];
  for (const [index, rawPoint] of rawSeries.entries()) {
    const point = asRecord(rawPoint);
    if (!point) {
      continue;
    }

    const timestamp = readTimestampMs(point.ts);
    const valueUsd = coerceFiniteNumber(
      typeof point.tvlUsd === "number" || typeof point.tvlUsd === "string"
        ? point.tvlUsd
        : undefined,
    );

    if (timestamp === undefined || valueUsd === undefined) {
      continue;
    }

    const eventType =
      typeof point.eventType === "string" ? point.eventType : null;
    const isLive = coerceBoolean(point.isLive, false);
    const fallbackId = isLive
      ? `${portfolioId}:live`
      : `${portfolioId}:${timestamp}:${eventType ?? "snapshot"}:${index}`;
    const id = readString(point.id) ?? fallbackId;

    mappedSeries.push({
      amountUsd:
        coerceFiniteNumber(
          typeof point.amount === "number" || typeof point.amount === "string"
            ? point.amount
            : undefined,
        ) ?? 0,
      cumulativeTwrPct: coerceFiniteNumber(
        typeof point.cumulativeTwrPct === "number" ||
          typeof point.cumulativeTwrPct === "string"
          ? point.cumulativeTwrPct
          : undefined,
      ),
      eventType,
      growthFactor: coerceFiniteNumber(
        typeof point.growthFactor === "number" ||
          typeof point.growthFactor === "string"
          ? point.growthFactor
          : undefined,
      ),
      id,
      isLive,
      portfolioId,
      timestamp,
      valueUsd,
    });
  }

  return mappedSeries;
}

export interface PerformanceLiveAssetCollectionModels {
  asset: PortfolioAssetModel;
  assetPrice: PortfolioAssetPriceModel;
  position: PortfolioPositionModel;
}

function readStrictDecimalNumber(value: unknown): number | undefined {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }

  const decimalString = readNonEmptyString(value);
  if (!decimalString || !STRICT_DECIMAL_PATTERN.test(decimalString)) {
    return undefined;
  }

  const parsedValue = Number(decimalString);
  return Number.isFinite(parsedValue) ? parsedValue : undefined;
}

function normalizeLegacyAssetIdCandidate(value: string): string | null {
  const normalized = tryAssetIdLikeToLegacyAssetId(value) ?? value;
  const nativeSlip44AssetId = normalizeNativeSlip44LegacyAssetId(normalized);
  if (nativeSlip44AssetId) {
    return nativeSlip44AssetId;
  }

  const parsedAsset = parsePortfolioAssetId(normalized);
  return parsedAsset && parsedAsset.vm !== "other:unknown"
    ? parsedAsset.id
    : null;
}

function normalizeNativeSlip44LegacyAssetId(value: string): string | null {
  const parsedAsset = parsePortfolioAssetId(value);
  if (!parsedAsset) {
    return null;
  }

  if (parsedAsset.address === EVM_NATIVE_SLIP44_REFERENCE) {
    return `${NATIVE_TOKEN_INTERNAL_PLACEHOLDER_ADDRESS}:${parsedAsset.chainRef}`;
  }

  if (parsedAsset.address === SOLANA_NATIVE_SLIP44_REFERENCE) {
    return `${WSOL_SOLANA_ADDRESS}:${SOLANA_CHAIN_ID}`;
  }

  return null;
}

function readLiveAssetIdentifierLegacyId(
  liveAsset: Record<string, unknown>,
): string | null {
  const assetNamespace = readNonEmptyString(liveAsset.assetNamespace);
  const assetReference = readNonEmptyString(liveAsset.assetReference);
  const chainNamespace = readNonEmptyString(liveAsset.chainNamespace);
  const chainReference = readNonEmptyString(liveAsset.chainReference);

  if (
    !assetNamespace ||
    !assetReference ||
    !chainNamespace ||
    !chainReference
  ) {
    return null;
  }

  if (
    chainNamespace === "eip155" &&
    assetNamespace === "slip44" &&
    assetReference === EVM_NATIVE_SLIP44_REFERENCE
  ) {
    return `${NATIVE_TOKEN_INTERNAL_PLACEHOLDER_ADDRESS}:${chainReference}`;
  }

  if (
    chainNamespace === "solana" &&
    assetNamespace === "slip44" &&
    assetReference === SOLANA_NATIVE_SLIP44_REFERENCE
  ) {
    return `${WSOL_SOLANA_ADDRESS}:${SOLANA_CHAIN_ID}`;
  }

  return normalizeLegacyAssetIdCandidate(
    `${chainNamespace}:${chainReference}/${assetNamespace}:${assetReference}`,
  );
}

function readLiveAssetLegacyId(
  liveAsset: Record<string, unknown>,
): string | null {
  const assetId = readNonEmptyString(liveAsset.assetId);
  if (assetId) {
    const normalizedAssetId = normalizeLegacyAssetIdCandidate(assetId);
    if (normalizedAssetId) {
      return normalizedAssetId;
    }
  }

  const identifierAssetId = readLiveAssetIdentifierLegacyId(liveAsset);
  if (identifierAssetId) {
    return identifierAssetId;
  }

  const caipAssetId = readNonEmptyString(liveAsset.caipAssetId);
  return caipAssetId ? normalizeLegacyAssetIdCandidate(caipAssetId) : null;
}

function areLiveValueFieldsConsistent(input: {
  computedValueUsd: number;
  explicitValueUsd?: number;
}): boolean {
  if (input.explicitValueUsd === undefined) {
    return true;
  }

  const toleranceUsd = Math.max(0.01, Math.abs(input.computedValueUsd) * 0.0001);
  return (
    Math.abs(input.explicitValueUsd - input.computedValueUsd) <= toleranceUsd
  );
}

export function mapPerformanceLiveAssetCollectionModel(
  portfolioId: string,
  rawLiveAsset: unknown,
  updatedAt: number,
): PerformanceLiveAssetCollectionModels | null {
  const liveAsset = asRecord(rawLiveAsset);
  if (!liveAsset) {
    return null;
  }

  const assetId = readLiveAssetLegacyId(liveAsset);
  const parsedAsset = assetId ? parsePortfolioAssetId(assetId) : null;
  const symbol = readNonEmptyString(liveAsset.symbol);
  const decimals = coerceFiniteInteger(liveAsset.decimals, -1);
  const quantity = readNonEmptyString(liveAsset.liveBalanceFormatted);
  const quantityNumber = readStrictDecimalNumber(quantity);
  const rawQuantity = readBigIntString(liveAsset.liveBalanceRaw);
  const priceUsd = readStrictDecimalNumber(liveAsset.priceUsd);
  const explicitValueUsd = readStrictDecimalNumber(liveAsset.valueUsd);

  if (
    !parsedAsset ||
    !symbol ||
    decimals < 0 ||
    !quantity ||
    quantityNumber === undefined ||
    quantityNumber <= 0 ||
    priceUsd === undefined ||
    priceUsd <= 0
  ) {
    return null;
  }
  const computedValueUsd = quantityNumber * priceUsd;
  if (
    !areLiveValueFieldsConsistent({
      computedValueUsd,
      explicitValueUsd,
    })
  ) {
    return null;
  }

  return {
    asset: {
      address: parsedAsset.address,
      chainRef: parsedAsset.chainRef,
      decimals,
      id: parsedAsset.id,
      name: symbol,
      symbol,
      vm: parsedAsset.vm,
    },
    assetPrice: {
      assetId: parsedAsset.id,
      id: parsedAsset.id,
      priceUsd,
      source: PERFORMANCE_LIVE_POSITION_SOURCE,
      updatedAt,
    },
    position: {
      assetId: parsedAsset.id,
      id: `${portfolioId}:${parsedAsset.id}`,
      portfolioId,
      quantity,
      ...(rawQuantity ? { rawQuantity } : {}),
      source: PERFORMANCE_LIVE_POSITION_SOURCE,
      updatedAt,
      valueUsd: computedValueUsd,
    },
  };
}

export function mapAllocationHistoryCheckpointModels(
  portfolioId: string,
  payload: PortfolioAllocationHistoryChartResponse,
): PortfolioAllocationHistoryCheckpointModel[] {
  return payload.checkpoints.map((checkpoint, index) => {
    const timestampMs = Date.parse(checkpoint.timestamp);
    const checkpointId =
      checkpoint.id.trim().length > 0
        ? checkpoint.id
        : `${checkpoint.timestamp}:${index}`;

    return {
      annotations: checkpoint.annotations.map((annotation) => ({
        eventId: annotation.eventId,
        id: annotation.id,
        statusLabel: annotation.statusLabel,
        timestamp: annotation.timestamp,
        title: annotation.title,
        type: annotation.type,
        workflowRunId: annotation.workflowRunId,
      })),
      assets: checkpoint.assets.map((asset) => ({
        allocationPct: asset.allocationPct,
        assetId: asset.assetId,
        marketValueUsd: asset.marketValueUsd,
        priceUsd: asset.priceUsd,
        quantity: asset.quantity,
        valuationResolved: asset.valuationResolved,
      })),
      checkpointId,
      eventIds: [...checkpoint.eventIds],
      id: `${portfolioId}:${checkpointId}`,
      isPartial: checkpoint.isPartial,
      portfolioId,
      timestamp: checkpoint.timestamp,
      timestampMs: Number.isFinite(timestampMs) ? timestampMs : 0,
      totalResolvedValueUsd: checkpoint.totalResolvedValueUsd,
      unresolvedAssetIds: checkpoint.unresolvedAssetIds.map(
        (assetId) => assetId,
      ),
    };
  });
}
